/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./Blog.css";

const blogs = [
  {
    id: 1,
    title: "How to Get Started with XRP and Mint Your First NFT on XRPL (Step-by-Step Guide for Beginners)",
    image: `${process.env.PUBLIC_URL}/blog/blog.png`,
    category: "Tutorial",
    date: "2025-06-15",
    readTime: "8 min read",
    featured: true,
    excerpt:
      "A complete beginner's guide to creating an XRPL wallet, purchasing XRP, and minting your first NFT on xrp.cafe—including the Sailing Ships collection.",
    content: `**How to Get Started with XRP and Mint Your First NFT on XRPL (Step-by-Step Guide for Beginners)**

The world of NFTs can feel overwhelming, especially if you're new to crypto. But the XRP Ledger (XRPL) makes it easier, faster, and more affordable to get started than many other blockchains. In this guide, we'll walk you through the three essential steps:

Creating an XRPL Wallet
Purchasing XRP
Connecting to xrp.cafe and minting your first NFT — including a special collection: Sailing Ships: Sailing Through Time & Dreams.

Let's set sail 🚢

**🧭 Step 1: Create Your XRPL Wallet**
To hold XRP and mint NFTs, you need an XRPL-compatible wallet. A popular and beginner-friendly option is:

**🔐 Xaman Wallet (formerly XUMM)**
Download the app from xaman.app

Install on mobile (iOS or Android)

Follow the steps to:

Create a new wallet (new account)

You will be presented with a set of secret numbers. The secret numbers consist of 8 rows, (A through H) with each row containing 6 digits. (0,1,2,3,-> 9) 

Back up your Secret Numbers (private key) (8 rows, (A through H) — store them securely and never share them with anyone!)

Set a passcode or biometric lock

🔁 Your wallet is now ready to receive XRP and interact with apps like xrp.cafe.

**💸 Step 2: Purchase XRP**
You'll need some XRP to activate your wallet and pay minting fees (XRPL fees are extremely low — a fraction of a cent).

**✅ Buy XRP via  XAMAN or a Crypto Exchange**

Via XAMAN xAPP

1. Open the Xaman app
2. Press the "Activate your account" button to enter the "Account Activation" flow.
3. Press the "Buy XRP" button
4. Follow the prompts to complete the purchase

You can also activate your account from an external source like a crypto exchange:

Uphold

Crypto.com

Binance

Kraken

Then follow these steps:

Complete identity verification (KYC)

Deposit funds (bank transfer, debit/credit card)

Buy XRP

Withdraw XRP to your XRPL wallet address:

Copy your wallet address from Xaman

Paste it into the withdrawal field on the exchange

No destination tag is needed unless the wallet you're transferring XRP to requires one

💡 Tip: The XRPL requires a 1 XRP reserve to activate your wallet. For minting and owning NFTs, you'll need a bit more to cover reserve requirements:

Start with 25–30 XRP

If you plan to hold many NFTs, budget more (each 32 NFTs = +2 XRP reserve)

**🌐 Step 3: Connect to xrp.cafe and Mint Your First NFT**
Now the fun begins! Let's mint your first NFT on the XRPL using xrp.cafe — a community-focused NFT marketplace built for XRP creators and collectors.

**🚪 1. Connect Your Wallet**
Go to xrp.cafe

Click "Connect Wallet"

Choose Xaman and scan the QR code with your mobile wallet app

🧭 You're now connected!

**🖼️ 2. 🚢 Mint a Sailing Ships NFT**
Explore the Sailing Ships: Sailing Through Time & Dreams collection — a cosmic and mythical NFT voyage filled with gemstone-crafted ships, stellar portals, and ancient lore.

To mint directly:

Visit the collection page: https://xrp.cafe/collection/sailing-ships

Browse available NFTs

Click "Buy" or "Mint", depending on the release format

Sign the transaction in Xaman to complete the purchase

⚓ You are now a sailor on the XRPL voyage.

**💬 Final Tips**
✅ Stay safe:
Never share your Secret Numbers. Always double-check URLs before connecting your wallet.

📲 Bookmark these tools:

[xaman.app](https://xaman.app) — Xaman Wallet

[xrp.cafe](https://xrp.cafe) — NFT minting and marketplace

[xrpscan.com](https://xrpscan.com) — View your wallet, NFTs, and transaction history

🌊 Join the community:
Follow XRPL creators on X (Twitter), and follow us @jubi_25 for XRP NFT updates.

**🎇 Ready to Mint?**
If you've followed these steps, you now have:

A secure XRPL wallet (Xaman)

XRP ready to spend

Full access to mint NFTs like the Sailing Ships Collection on xrp.cafe

This is just the beginning of your voyage through art, imagination, and the power of decentralized tech.

Welcome aboard. 🌌⛵`,
  },
  {
    id: 2,
    title: "Unveiling the Vision of Sailing Ships",
    image: `${process.env.PUBLIC_URL}/blog/blog1.png`,
    category: "Vision",
    date: "2025-06-20",
    readTime: "6 min read",
    featured: false,
    excerpt:
      "A nostalgic voyage from childhood wonder to AI-powered imagination — discover how Sailing Ships invites you to co-navigate a dreamlike digital ocean.",
    content: `🚢 **Behind the Pixels: The Story and Vision of Sailing Ships**

**1. Origin Story: A Postcard & a Dream**
"As a child, I received a postcard showing a majestic sailing ship under a star-filled sky. I stared at it for hours, imagining the creak of wood, the swaying deck, and the hush of the wind."
That memory stayed with me—fueling a lifelong fascination with voyages and wonder. Sailing Ships revives that sense of cosmic exploration and invites others to feel it too.

**2. The Vision: Dream-Like Journeys**
At its core, Sailing Ships is more than digital art—it's an invitation to dream. When you hold one of these NFTs, you become a navigator on an ethereal voyage:

- **Embrace wonder:** Feel the pull of imagination
- **Foster creativity:** Bring your narrative
- **Build community:** Journey as a collective crew

**3. Creative Process: AI + Human Touch**
Each piece starts with carefully crafted prompts—phrases like "a ship gliding through mist under a galaxy of stars." I then rely on AI generation, blending:

- Vivid natural textures
- Mystical lighting and ambiance
- Evocative color palettes

This fusion of human direction and AI creativity breathes life into each dreamlike seascape.

**4. Roadmap & Utility: What Lies Ahead**

| Phase            | Details                                                                 |
|------------------|-------------------------------------------------------------------------|
| Genesis Drop     | Unlocks your access to the Sailing Ships universe                       |
| Future Airdrops  | Holders receive exclusive NFTs from upcoming collections                |
| Collaborations   | Expect art, music & conservation-themed partnerships                    |
| Virtual Events   | Holder‑only gatherings: starlit gallery showings, Q&As, socials         |

Genesis holders enjoy early access and are at the heart of everything.

**5. Why It Matters**
In a sea of profile-picture NFTs, Sailing Ships stands apart:

- It prioritizes emotion over branding
- Creates a space for narrative-driven art
- Fosters an inclusive, creative community

This isn't a static drop—it's an ongoing voyage, and your involvement shapes its trajectory.

**6. Join the Crew: How to Get Onboard**

1. **Explore the Collection** – Browse Genesis artworks
2. **Collect Your Ship** – Become a Genesis holder
3. **Join the Community** – Follow on X, stay updated
4. **Share the Dream** – Tag #SailingShipsNFT and inspire others
5. **Stay Updated** – Subscribe for airdrop alerts and roadmap news

**7. Frequently Asked Questions**

**Q: What tech powers the art?**  
A: The pieces are generated with AI, using rich, evocative prompts I curate.

**Q: How do airdrops work?**  
A: If you own a Genesis NFT, future drops are automatically delivered to your wallet.

**Q: How can I participate in events?**  
A: All announcements and event links are shared on our X account—follow @jubi_25 for the latest updates.

**Final Call to Action**  
Chart your course and embark on a journey of wonder.  
[Discover the Genesis Collection →](https://xrp.cafe/collection/sailing-ships)  
[Follow us on X →](https://x.com/jubi_25)`,
  },
  {
    id: 3,
    title: '"AI-Made" Isn\'t Effortless: The Reality Behind Crafting NFT Collections with Artificial Intelligence',
    image: `${process.env.PUBLIC_URL}/blog/blog3.png`,
    category: "Behind the Scenes",
    date: "2025-07-04",
    readTime: "7 min read",
    featured: false,
    excerpt:
      "Unveiling the truth behind AI-generated NFTs: the countless hours, technical challenges, and creative decisions that go into building a serious collection.",
    content: `**"AI-Made" Isn't Effortless: The Reality Behind Crafting NFT Collections with Artificial Intelligence**

In the age of artificial intelligence, there's a common misconception floating around: that AI-generated NFTs are easy. That all it takes is typing a few words into a prompt box, clicking "generate," and voilà—a fully-formed, ready-to-mint masterpiece appears. For anyone who's spent time building a serious collection like Sailing Ships: Sailing Through Time & Dreams, this couldn't be further from the truth.

As the creator behind a cosmic, mythical, and meticulously designed NFT universe, I've experienced firsthand how much real human effort it takes to bring AI-generated art to life. Every gemstone ship in the Sailing Ships collection isn't just a product of machine imagination—it's a result of layered, structured thought, storytelling, and technical orchestration. And yes, the AI is a tool—but like any tool, it's only as effective as the hands that wield it.

Let's break it down.

**Prompt Crafting Is an Artform, Not a Shortcut**

Creating a visually compelling NFT isn't about tossing in a random prompt. For Sailing Ships, I've gone through hundreds—if not thousands—of prompt variations. Each one refined to balance surrealism with narrative consistency: gemstone hulls, cosmic tides, stellar portals, and mythological backstories woven into every detail.

The angle of the ship. The thrust of its engine. The emotion it evokes. The story it belongs to. These aren't accidents. They're choices. Iterated. Refined. Tested.

An AI tool may generate the pixels, but it's up to the creator to train the tool, not the other way around.

**Metadata: The Backbone Most People Ignore**

After the visuals come the spreadsheets. AI-generated NFTs still need clean, structured, searchable metadata. Traits need to be defined. Tiers need to be organized. Rarity has to be calculated. JSON schemas must match the minting protocol—in my case, XRPL.

In Sailing Ships, each NFT is more than a pretty image. It has metadata that includes its era, elemental affinity, hull material, cosmic origin, ship class, special lore markers, and more. And that metadata isn't generated automatically. It's parsed, labeled, mapped, sometimes even validated by AI pipelines I've built myself.

Then comes integration with smart contracts. Testing, deploying, verifying IPFS or Arweave links. One missed field? The mint can fail. That's not magic—that's engineering.

**Storytelling Still Matters**

The Sailing Ships universe isn't just a gallery—it's a mythos. Each vessel has its lore: forged in supernovae, piloted by cosmic titans, sailing through interstellar wormholes to protect ancient treasure. These stories aren't written by AI in one go. They're pieced together across timelines and themes, often inspired by childhood memories, nautical legends, and sci-fi dreams.

AI helps accelerate ideas, but it doesn't replace the vision. Worldbuilding still takes soul.

**The Grind Behind the Glamour**

What people don't see:

- Hours spent fixing background transparency for PNGs
- Renaming thousands of files by hand or script
- Building custom Python tools to check for duplicates, shuffle traits, and auto-generate metadata
- Creating teaser trailers, lore documents, and social content to accompany each launch
- Debugging JSON errors at midnight
- Re-generating a single image 14 times just to get the glow on a gemstone just right

AI might save time, but building a true NFT brand still requires the blood, sweat, and cosmic vision of a committed creator.

**In Conclusion**

So yes—AI has changed the game for digital art and NFTs. It empowers creators like me to build expansive universes faster than ever before. But don't let the tech fool you: true creativity is still human-powered. The AI is a co-pilot, not the captain.

When you look at a ship in the Sailing Ships collection, you're not just seeing AI art. You're seeing dozens of decisions layered into a single image, backed by code, lore, and vision.

That's the real voyage—and it's far from simple.`,
  },
  {
    id: 4,
    title: "Charting New Waters: The Future of NFT Art",
    image: `${process.env.PUBLIC_URL}/blog/blog2.png`,
    category: "Industry",
    date: "2025-07-10",
    readTime: "4 min read",
    featured: false,
    excerpt:
      "Explore how NFT art is evolving, what makes Sailing Ships unique, and what's next for digital creators in the blockchain space.",
    content: `**Charting New Waters: The Future of NFT Art**

The NFT landscape has evolved dramatically since its explosive debut. What started as simple profile pictures and digital collectibles has transformed into a sophisticated ecosystem of art, utility, and community. As we sail into 2025, the waters of digital art are changing, and projects like Sailing Ships are leading the way.

**The Evolution Beyond JPEGs**

Early NFTs were often criticized as overpriced images with little inherent value. Today, successful projects understand that lasting value comes from:

- **Narrative depth:** Every piece tells a story
- **Community building:** Holders become part of something larger
- **Utility and experiences:** NFTs unlock access to events, collaborations, and future drops
- **Artistic merit:** Quality and creativity matter more than hype

**What Makes Sailing Ships Different**

In a sea of profile-picture projects and cash-grab collections, Sailing Ships charts a different course:

**Story-Driven Collections**
Each ship in our collection isn't just a random generation—it's a vessel with history, purpose, and mythological significance. From gemstone hulls forged in supernovae to cosmic navigation systems, every element serves the larger narrative.

**AI-Assisted Creativity**
We embrace AI as a creative partner, not a replacement for human vision. The technology allows us to explore visual possibilities that would be impossible through traditional means, while human curation ensures quality and coherence.

**Community-Centric Approach**
Our holders aren't just buyers—they're crew members on an ongoing voyage. Regular events, collaborative storytelling, and holder-exclusive content create genuine value beyond the initial purchase.

**Looking Ahead: What's Next**

The future of NFT art lies in several key areas:

**1. Interoperability**
Cross-chain compatibility and universal standards will make NFTs more accessible and valuable across different platforms and ecosystems.

**2. Physical-Digital Bridge**
The line between digital and physical art will continue to blur, with NFTs serving as certificates for real-world items and experiences.

**3. AI Integration**
More sophisticated AI tools will democratize creation while raising important questions about authorship and value.

**4. Sustainable Practices**
Environmentally conscious blockchains like XRPL are becoming preferred platforms for creators who care about their ecological footprint.

**Join the Voyage**

As we navigate these new waters, projects that prioritize community, creativity, and genuine value will rise above the noise. Sailing Ships represents this new wave of thoughtful, narrative-driven NFT art.

Whether you're a seasoned collector or new to the space, there's never been a better time to explore what's possible when technology meets imagination.

Ready to set sail? The horizon holds infinite possibilities.`,
  },
];

export default function Blog({ open, onClose }) {
  const [selected, setSelected] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Get unique categories
  const categories = ["All", ...new Set(blogs.map((blog) => blog.category))];

  // Filter blogs by category and featured status
  const filteredBlogs =
    selectedCategory === "All"
      ? [...blogs].sort((a, b) => {
          // Featured posts first, then by date (newest first)
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return new Date(b.date) - new Date(a.date);
        })
      : blogs.filter((blog) => blog.category === selectedCategory).sort((a, b) => new Date(b.date) - new Date(a.date));

  // Format date for display
  const formatDate = (dateString) => {
    // Parse date as local timezone to avoid timezone conversion issues
    const [year, month, day] = dateString.split("-");
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Scroll lock when blog modal is open
  useEffect(() => {
    if (open) {
      // Store current scroll position
      const currentScrollY = window.scrollY;

      // Apply scroll lock with preserved position
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = "100%";
      document.body.classList.add("modal-open");

      // Add keyboard listener for Escape key
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          if (enlargedImage) {
            setEnlargedImage(null);
          } else {
            onClose();
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        // Restore scroll position and remove lock
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.classList.remove("modal-open");
        window.scrollTo(0, currentScrollY);
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [open, onClose, enlargedImage]);

  if (!open) return null;

  // Simple Markdown to HTML converter for bold, italics, lists, and links
  function renderMarkdown(md) {
    let html = md
      .replace(/^### (.*$)/gim, "<h3>$1</h3>")
      .replace(/^## (.*$)/gim, "<h2>$1</h2>")
      .replace(/^# (.*$)/gim, "<h1>$1</h1>")
      .replace(/\*\*(.*?)\*\*/gim, "<b>$1</b>")
      .replace(/\*(.*?)\*/gim, "<i>$1</i>")
      .replace(/\n-\s(.*)/g, "<li>$1</li>")
      .replace(/\n\d+\.\s(.*)/g, "<li>$1</li>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank">$1</a>')
      .replace(/\n/g, "<br />");
    // Wrap lists in <ul> or <ol>
    html = html.replace(/(<br \/>)*(<li>.*?<\/li>)+/g, (m) => `<ul>${m.replace(/<br \/>/g, "")}</ul>`);
    return html;
  }

  return (
    <div className="blog-modal-bg">
      <button
        className="modal-backdrop-button"
        onClick={onClose}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "transparent",
          border: "none",
          cursor: "default",
          zIndex: -1,
        }}
        aria-label="Close modal"
        tabIndex={-1}
      />
      <div className="blog-modal">
        <button className="blog-modal-close" onClick={onClose}>
          ✕
        </button>
        <h2 className="blog-modal-title">
          <span>Blog</span>
        </h2>

        {/* Category Filter */}
        <div className="blog-category-filter">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? "active" : ""}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="blog-cards">
          {filteredBlogs.map((blog) => (
            <button
              className={`blog-card ${blog.featured ? "featured" : ""}`}
              key={blog.id}
              onClick={() => setSelected(blog.id)}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                display: "block",
                width: "100%",
                textAlign: "left",
              }}
              aria-label={`Read ${blog.title}`}
            >
              {blog.featured && <div className="featured-badge">Featured</div>}
              <img src={blog.image} alt={blog.title} className="blog-card-img" />
              <div className="blog-card-content">
                <div className="blog-meta">
                  <span className="blog-category">{blog.category}</span>
                  <span className="blog-date">{formatDate(blog.date)}</span>
                </div>
                <h3>{blog.title}</h3>
                <p>{blog.excerpt}</p>
                <div className="blog-read-time">{blog.readTime}</div>
              </div>
            </button>
          ))}
        </div>

        {selected !== null && (
          <div className="blog-detail-modal">
            <button
              className="modal-backdrop-button"
              onClick={() => setSelected(null)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "transparent",
                border: "none",
                cursor: "default",
                zIndex: -1,
              }}
              aria-label="Close blog detail"
              tabIndex={-1}
            />
            <div className="blog-detail-content">
              <button className="blog-modal-close" onClick={() => setSelected(null)}>
                ✕
              </button>
              {(() => {
                const selectedBlog = blogs.find((blog) => blog.id === selected);
                return selectedBlog ? (
                  <>
                    <div className="blog-detail-header">
                      <div className="blog-detail-meta">
                        <span className="blog-category">{selectedBlog.category}</span>
                        <span className="blog-date">{formatDate(selectedBlog.date)}</span>
                        <span className="blog-read-time">{selectedBlog.readTime}</span>
                      </div>
                      <h3>{selectedBlog.title}</h3>
                    </div>
                    <button
                      onClick={() => setEnlargedImage(selectedBlog.image)}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: "zoom-in",
                        display: "block",
                      }}
                      title="Click to enlarge"
                      aria-label={`Enlarge ${selectedBlog.title} image`}
                    >
                      <img
                        src={selectedBlog.image}
                        alt={selectedBlog.title}
                        className="blog-detail-image"
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </button>
                    <div
                      className="blog-detail-body"
                      dangerouslySetInnerHTML={{
                        __html: renderMarkdown(selectedBlog.content),
                      }}
                    />
                  </>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* Enlarged Image Modal */}
        {enlargedImage && (
          <div
            className="image-lightbox-bg"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 10001,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "fadeIn 0.2s",
            }}
          >
            <button
              className="modal-backdrop-button"
              onClick={() => setEnlargedImage(null)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setEnlargedImage(null);
                }
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "transparent",
                border: "none",
                cursor: "default",
                zIndex: -1,
              }}
              aria-label="Close lightbox"
              tabIndex={-1}
            />
            <div
              style={{
                position: "relative",
                maxWidth: "60vw",
                maxHeight: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    maxWidth: "70%",
                    maxHeight: "70%",
                  }}
                >
                  <h2 id="lightbox-title" style={{ position: "absolute", left: "-9999px" }}>
                    Enlarged image view
                  </h2>
                  <img
                    src={enlargedImage}
                    alt="Enlarged view"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
                      display: "block",
                      border: "3px solid #ffd700",
                    }}
                  />
                  <button
                    onClick={() => setEnlargedImage(null)}
                    className="blog-modal-close"
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      background: "none",
                      border: "none",
                      color: "#FFD700",
                      fontSize: "2rem",
                      cursor: "pointer",
                      zIndex: 1,
                      transition: "color 0.2s ease",
                    }}
                    title="Close enlarged image"
                    aria-label="Close enlarged image"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

Blog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
